// 1. React
import { memo, useEffect, useState, useMemo } from "react";
// 2. Helpers
import { useTranslate } from "helpers";

// 3. Types
import { InvoiceHeaderProps } from "./types";
import { InvoiceOutgoingItemRow } from "services/api/fleet/vehicles/invoices/types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

// 4. Form hooks
import {
    useHandleDateChange,
    useHandleDropdownChange,
    useHandleNumberFieldChange,
} from "services/forms/hooks";

// 5. API / Consts
import {
    getBuyerTypesAPI,
    getPaymentMethodsAPI,
    getTaxTypesAPI,
    getTaxExemptionReasonsAPI,
} from "services/api/finance/lookups/api";
import { getFleetVehicleOfferSaleAPI } from "services/api/fleet/vehicles/offers/api";
import { FleetVehicleContractSaleRow } from "services/api/fleet/vehicles/contracts/types";

// 6. UI Components
import FleetContractSaleDropdown from "ui/components/Dropdown/FleetContractSaleDropdown/FleetContractSaleDropdown";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DatePicker } from "ui/components/DatePicker";
import { NumberField } from "ui/components/TextField/NumberField";


const InvoiceHeader: React.FC<InvoiceHeaderProps> = (props) => {
    const { vehicleId, formValues, setFormValues, errors, setIsLoading, onContractSalesLoaded } = props;

    const { t } = useTranslate();

    const [buyerTypeOptions, setBuyerTypeOptions] = useState<DropdownOption[]>([]);
    const [paymentMethodOptions, setPaymentMethodOptions] = useState<DropdownOption[]>([]);
    const [taxTypeOptions, setTaxTypeOptions] = useState<DropdownOption[]>([]);
    const [allExemptionReasons, setAllExemptionReasons] = useState<any[]>([]);


    useEffect(() => {
        fetchLookups();
    }, []);


    const handleContractSelect = async (contract: FleetVehicleContractSaleRow | null) => {
        if (!contract) return;

        if (setIsLoading) setIsLoading(true);

        // Determine buyer type from contact type: P (person) → b2c, C (company) → b2b
        const contactTypeId = contract.buyerContactTypeId ?? contract.offerSale?.contactClient?.contactType?.id;
        const defaultBuyerTypeId = contactTypeId === "P" ? "b2c" : contactTypeId === "C" ? "b2b" : null;

        // Set contract data immediately
        setFormValues((prev) => ({
            ...prev,
            contractSaleId: contract.id,
            vehicleId: contract.offerSale?.vehicle?.id ?? prev.vehicleId,
            contactClientId: contract.buyerContactId ?? contract.offerSale?.contactClient?.id ?? prev.contactClientId,
            dueDate: contract.offerSale?.dueDate ?? prev.dueDate,
            durationDays: contract.offerSale?.durationDays ?? prev.durationDays,
            buyerTypeId: defaultBuyerTypeId ?? prev.buyerTypeId,
            buyerName: contract.buyerName ?? prev.buyerName,
            buyerAddress: contract.buyerAddress ?? prev.buyerAddress,
            buyerOIB: contract.buyerPersonalIdentificationNumber ?? prev.buyerOIB,
        }));

        // Fetch offer to get items
        if (contract.offerSale?.id) {
            try {
                const offer = await getFleetVehicleOfferSaleAPI(contract.offerSale.id);
                const offerItems = offer.items?.filter((i) => !i.deleted) ?? [];
                const invoiceItems: InvoiceOutgoingItemRow[] = offerItems.map((i) => ({
                    id: crypto.randomUUID(),
                    code: i.code,
                    name: i.name,
                    taxRate: i.taxRate,
                    quantity: i.quantity,
                    priceWithoutTax: i.priceWithoutTax,
                    priceWithTax: i.priceWithTax,
                    discountAmount: i.discountAmount,
                    discountTypeId: i.discountTypeId,
                    total: i.total,
                    deleted: false,
                    note: i.note ?? i.childRows?.[0]?.note ?? null,
                    childRows: i.childRows?.map((c) => ({ note: c.note })) ?? [],
                    // Vrsta stavke, KPD i JM prenose se s ponude — KPD je obavezan na eRačunu.
                    billingItemTypeId: i.billingItemTypeId,
                    kpdCode: i.kpdCode,
                    unitOfMeasureId: i.unitOfMeasureId,
                    discountPercent: null,
                }));
                setFormValues((prev) => ({ ...prev, items: invoiceItems }));
            } catch {
                // offer fetch failed, items stay empty
            }
        }

        if (setIsLoading) setIsLoading(false);
    };

    const fetchLookups = async () => {
        try {
            const [buyerTypes, paymentMethods, taxTypes, exemptionReasons] = await Promise.all([
                getBuyerTypesAPI(),
                getPaymentMethodsAPI(),
                getTaxTypesAPI(),
                getTaxExemptionReasonsAPI(),
            ]);

            setBuyerTypeOptions(buyerTypes?.map((item: any) => ({
                text: (<div className="flex items-center justify-between w-full">
                    <span>{item.name}</span>
                    <span className="text-xs bg-blue-200 text-blue-900 py-0.5 px-2 rounded-sm block ml-2.5">{item.description}</span>
                </div>),
                value: item.id,
            })) ?? []);
            setPaymentMethodOptions(paymentMethods?.map((item: any) => ({ text: item.name, value: item.id })) ?? []);
            setTaxTypeOptions(taxTypes?.map((item: any) => ({ text: item.name, value: item.id })) ?? []);
            setAllExemptionReasons(exemptionReasons ?? []);
        } catch {
            // error handled by snackbar
        }
    };

    const exemptionReasonOptions = useMemo<DropdownOption[]>(() => {
        if (!formValues.taxTypeId) return [];
        return allExemptionReasons
            .filter((item: any) => item.taxTypeId === formValues.taxTypeId)
            .map((item: any) => ({ text: item.name, value: item.id }));
    }, [allExemptionReasons, formValues.taxTypeId]);

    const handleTaxTypeChange = (_e: any, _name: string, item: DropdownOption) => {
        setFormValues(prev => ({
            ...prev,
            taxTypeId: item.value,
            taxExemptionReasonId: null,
        }));
    };

    const handleDropdownChange = useHandleDropdownChange(
        setFormValues,
        formValues
    );

    const handleDateChange = useHandleDateChange(setFormValues, formValues);

    const handleNumberFieldChange = useHandleNumberFieldChange(
        setFormValues,
        formValues
    );

    return (
        <>
            <div className="flex flex-col gap-3 mb-3">
                <div className="flex gap-4">
                    <div className="flex-1">
                        <FleetContractSaleDropdown
                            name="contractSaleId"
                            vehicleId={vehicleId}
                            placeholder={t("admin.fleet.vehicle.invoice.add.contract.placeholder")}
                            label={t("admin.fleet.vehicle.invoice.add.contract")}
                            onChange={handleDropdownChange}
                            onContractSelect={handleContractSelect}
                            value={formValues.contractSaleId}
                            required
                            showLinkedToInvoice={true}
                            showCancelled={false}
                            autoSelectLast={!formValues.id}
                            disabled={!!formValues.id}
                            onLoaded={onContractSalesLoaded}
                        />
                    </div>
                    <div className="flex-1">
                        {formValues.contractSaleId && (

                            <Dropdown
                                name="buyerTypeId"
                                placeholder={t("admin.fleet.vehicle.invoice.add.buyertype.placeholder")}
                                label={t("admin.fleet.vehicle.invoice.add.buyertype")}
                                options={buyerTypeOptions}
                                onChange={handleDropdownChange}
                                value={formValues.buyerTypeId}
                            />

                        )}
                    </div>
                    <div className="flex-1">
                        <Dropdown
                            name="paymentMethodId"
                            placeholder={t("admin.fleet.vehicle.invoice.add.paymentmethod.placeholder")}
                            label={t("admin.fleet.vehicle.invoice.add.paymentmethod")}
                            options={paymentMethodOptions}
                            onChange={handleDropdownChange}
                            value={formValues.paymentMethodId}
                        />
                    </div>
                </div>

                {formValues.contractSaleId && (
                    <>

                        <div className="flex gap-4">
                            <div className="flex-1">
                                <DatePicker
                                    name="deliveryDate"
                                    label={t("admin.fleet.vehicle.invoice.add.deliverydate")}
                                    placeholder={t("admin.fleet.vehicle.invoice.add.deliverydate.placeholder")}
                                    onChange={handleDateChange}
                                    value={formValues.deliveryDate}
                                    width="full"
                                    minDate={new Date()}
                                />
                            </div>
                            <div className="flex-1">
                                <DatePicker
                                    name="dueDate"
                                    label={t("admin.fleet.vehicle.invoice.add.duedate")}
                                    placeholder={t(
                                        "admin.fleet.vehicle.invoice.add.duedate.placeholder"
                                    )}
                                    onChange={handleDateChange}
                                    error={errors.dueDate}
                                    value={formValues.dueDate}
                                    width="full"
                                    required
                                    minDate={new Date()}
                                />
                            </div>
                            <div className="flex flex-1 items-end">
                                <label className="block text-sm mb-2 mr-3 font-medium leading-6 text-[#344054]">
                                    {t("admin.fleet.vehicle.invoice.add.durationdays")}
                                </label>
                                <div className="flex-1">
                                    <NumberField
                                        name="durationDays"
                                        onChange={handleNumberFieldChange}
                                        value={formValues.durationDays}
                                        decimals={0}
                                    ></NumberField>
                                </div>
                            </div>

                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Dropdown
                                    name="taxTypeId"
                                    placeholder="Odaberite vrstu obračuna"
                                    label="Vrsta obračuna PDV-a"
                                    options={taxTypeOptions}
                                    onChange={handleTaxTypeChange}
                                    value={formValues.taxTypeId}
                                />
                            </div>
                            <div className="col-span-2">
                                {formValues.taxTypeId && (
                                    <Dropdown
                                        name="taxExemptionReasonId"
                                        placeholder="Odaberite razlog oslobođenja"
                                        label="Razlog oslobođenja"
                                        options={exemptionReasonOptions}
                                        onChange={handleDropdownChange}
                                        value={formValues.taxExemptionReasonId}
                                    />
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default memo(InvoiceHeader);
